import { useEffect, useState } from "react";
import {
  Activity,
  BarChart3,
  BookOpen,
  Brain,
  CheckCircle2,
  FlaskConical,
  GitBranch,
  MessageSquareText,
  PackageCheck,
  Search,
  ShieldCheck,
  Sparkles,
  Target,
  X,
} from "lucide-react";
import api from "../lib/api";
import TopBar from "../components/TopBar";
import EmptyState from "../components/EmptyState";

const DEPT_ICONS = {
  "program-office": Target,
  product: Sparkles,
  research: Search,
  design: FlaskConical,
  engineering: GitBranch,
  qa: CheckCircle2,
  security: ShieldCheck,
  release: PackageCheck,
  growth: BarChart3,
  analytics: Activity,
  "customer-voice": MessageSquareText,
  knowledge: BookOpen,
};

export default function DepartmentsPage() {
  const [departments, setDepartments] = useState([]);
  const [agentsByKey, setAgentsByKey] = useState({});
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      const { data: dpts } = await api.get("/departments");
      setDepartments(dpts);
      const results = await Promise.all(
        dpts.map((d) => api.get(`/departments/${d.key}/agents`).then((r) => [d.key, r.data]))
      );
      setAgentsByKey(Object.fromEntries(results));
    })();
  }, []);

  return (
    <>
      <TopBar
        title="AI Workforce"
        subtitle="Twelve specialized departments. Bounded scope. Explicit approvals."
      />
      <div className="dept-strip" style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}>
        {departments.map((d) => {
          const Icon = DEPT_ICONS[d.key] || Sparkles;
          const agents = agentsByKey[d.key] || [];
          return (
            <button
              key={d.key}
              className="dept-chip"
              onClick={() => setSelected(d)}
              data-testid={`dept-card-${d.key}`}
              style={{ textAlign: "left", cursor: "pointer" }}
            >
              {d.can_block_release ? <span className="gate-badge">Release Gate</span> : null}
              <div className="icon"><Icon size={18} /></div>
              <b>{d.name}</b>
              <small>{d.purpose}</small>
              <div className="hint" style={{ marginTop: 8, fontSize: 10 }}>
                {agents.length} agent{agents.length === 1 ? "" : "s"}
              </div>
            </button>
          );
        })}
      </div>

      {selected ? (
        <DrawerDetail
          dept={selected}
          agents={agentsByKey[selected.key] || []}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </>
  );
}

function DrawerDetail({ dept, agents, onClose }) {
  return (
    <div className="modal-backdrop" onMouseDown={onClose} data-testid="dept-drawer">
      <section className="modal" onMouseDown={(e) => e.stopPropagation()} style={{ width: "min(720px,100%)" }}>
        <button className="close" onClick={onClose}><X size={18} /></button>
        <span className="eyebrow">{dept.can_block_release ? "RELEASE GATE" : "DEPARTMENT"}</span>
        <h2>{dept.name}</h2>
        <p className="hint">{dept.purpose}</p>
        <div className="detail-meta">
          <div className="cell"><small>Key</small><b className="mono">{dept.key}</b></div>
          <div className="cell"><small>Output</small><b>{dept.output}</b></div>
          <div className="cell"><small>Order</small><b>#{dept.order + 1}</b></div>
          <div className="cell"><small>Can block release</small><b>{dept.can_block_release ? "yes" : "no"}</b></div>
        </div>
        <div className="section-heading"><h3>Agents</h3><span className="hint">{agents.length}</span></div>
        {agents.length === 0 ? <EmptyState title="No agents configured." /> :
          agents.map((a) => (
            <div className="wide-row" key={a.id}>
              <div>
                <b>{a.name}</b>
                <p className="mono" style={{ fontSize: 11 }}>{a.provider} · {a.model}</p>
                <small>Capabilities: {a.capabilities.join(", ") || "—"}</small>
                <div><small>Approvals: {a.requires_approval_for.join(", ") || "none"}</small></div>
              </div>
              <span className={`pill ${a.active ? "green" : "dim"}`}>{a.active ? "active" : "inactive"}</span>
            </div>
          ))
        }
      </section>
    </div>
  );
}
